import type { Community } from "../../../../types/Community";

export type StylePackKey =
  | "classic"
  | "energetic"
  | "zen"
  | "editorial"
  | "calmPower";

export type FontThemeKey =
  | "default"
  | "exercise"
  | "meditation"
  | "diet"
  | "learning"
  | "serif"
  | "geometric"
  | "humanist"
  | "power"
  | "stressFluent";

export type PersonalizationDraft = {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  surfaceColor: string;
  surfaceElevatedColor: string;
  surfaceBorderColor: string;
  dividerColor: string;
  textColor: string;
  secondaryTextColor: string;
  mutedTextColor: string;
  onAccentColor: string;
  fontTheme: string;
  stylePack: string;
  typographyScale: number | "";
  radiusScale: number | "";
};

export type StylePackPreview = {
  key: StylePackKey;
  label: string;
  hint: string;
  palette: {
    background: string;
    surface: string;
    surfaceElevated: string;
    surfaceBorder: string;
    divider: string;
    textPrimary: string;
    textSecondary: string;
    textMuted: string;
    onPrimary: string;
  };
  shape: {
    radiusSm: number;
    radiusMd: number;
    radiusLg: number;
    buttonRadius: number;
  };
  depth: "flat" | "shadow";
};

export const STYLE_PACKS: Record<StylePackKey, StylePackPreview> = {
  classic: {
    key: "classic",
    label: "Classic",
    hint: "Balanced rounding with soft depth",
    palette: {
      background: "#FAF3E0",
      surface: "#FFFFFF",
      surfaceElevated: "#FFFFFF",
      surfaceBorder: "#E8E8E8",
      divider: "#EFEBE0",
      textPrimary: "#1A1A1A",
      textSecondary: "#595959",
      textMuted: "#999999",
      onPrimary: "#FFFFFF",
    },
    shape: { radiusSm: 6, radiusMd: 16, radiusLg: 24, buttonRadius: 16 },
    depth: "shadow",
  },
  energetic: {
    key: "energetic",
    label: "Energetic",
    hint: "Tighter corners with stronger depth",
    palette: {
      background: "#F4F5F7",
      surface: "#FFFFFF",
      surfaceElevated: "#FFFFFF",
      surfaceBorder: "#DDE1E6",
      divider: "#E9ECF0",
      textPrimary: "#0E1116",
      textSecondary: "#3D434C",
      textMuted: "#8A9099",
      onPrimary: "#FFFFFF",
    },
    shape: { radiusSm: 6, radiusMd: 12, radiusLg: 16, buttonRadius: 12 },
    depth: "shadow",
  },
  zen: {
    key: "zen",
    label: "Zen",
    hint: "Generous curves with flat warm surfaces",
    palette: {
      background: "#FAF7F2",
      surface: "#FFFDFA",
      surfaceElevated: "#FFFFFF",
      surfaceBorder: "#EDE7DC",
      divider: "#F2EEE5",
      textPrimary: "#3A362F",
      textSecondary: "#6B655B",
      textMuted: "#A39C8F",
      onPrimary: "#FFFFFF",
    },
    shape: { radiusSm: 14, radiusMd: 20, radiusLg: 28, buttonRadius: 28 },
    depth: "flat",
  },
  editorial: {
    key: "editorial",
    label: "Editorial",
    hint: "Crisp corners with quiet borders",
    palette: {
      background: "#FFFFFF",
      surface: "#FFFFFF",
      surfaceElevated: "#F7F7F7",
      surfaceBorder: "#D6D6D6",
      divider: "#E8E8E8",
      textPrimary: "#111111",
      textSecondary: "#444444",
      textMuted: "#8F8F8F",
      onPrimary: "#FFFFFF",
    },
    shape: { radiusSm: 4, radiusMd: 8, radiusLg: 12, buttonRadius: 8 },
    depth: "flat",
  },
  calmPower: {
    key: "calmPower",
    label: "Calm Power",
    hint: "Warm ivory with composed rounded forms",
    palette: {
      background: "#F7F4EE",
      surface: "#FDFCF9",
      surfaceElevated: "#FFFFFF",
      surfaceBorder: "#E0DAD0",
      divider: "#ECE6DB",
      textPrimary: "#1A1A18",
      textSecondary: "#4A4A45",
      textMuted: "#8A8A82",
      onPrimary: "#FDFCF9",
    },
    shape: { radiusSm: 8, radiusMd: 16, radiusLg: 24, buttonRadius: 14 },
    depth: "flat",
  },
};

export const STYLE_PACK_OPTIONS = Object.values(STYLE_PACKS);

export const FONT_THEME_OPTIONS: Array<{
  value: FontThemeKey;
  label: string;
  hint: string;
  previewFamily: string;
}> = [
  {
    value: "default",
    label: "Circular",
    hint: "Neutral product sans",
    previewFamily: "Arial, sans-serif",
  },
  {
    value: "exercise",
    label: "Archivo",
    hint: "Athletic and compact",
    previewFamily: "Arial Narrow, Arial, sans-serif",
  },
  {
    value: "meditation",
    label: "Lora",
    hint: "Reflective serif",
    previewFamily: "Georgia, serif",
  },
  {
    value: "diet",
    label: "Nunito",
    hint: "Friendly rounded sans",
    previewFamily: "Trebuchet MS, sans-serif",
  },
  {
    value: "learning",
    label: "Manrope",
    hint: "Clear contemporary sans",
    previewFamily: "Arial, sans-serif",
  },
  {
    value: "serif",
    label: "Fraunces",
    hint: "Premium editorial serif",
    previewFamily: "Georgia, serif",
  },
  {
    value: "geometric",
    label: "Sora",
    hint: "Modern geometric sans",
    previewFamily: "Century Gothic, Arial, sans-serif",
  },
  {
    value: "humanist",
    label: "Figtree",
    hint: "Warm humanist sans",
    previewFamily: "Segoe UI, Arial, sans-serif",
  },
  {
    value: "power",
    label: "Barlow Condensed",
    hint: "Tall, forceful display",
    previewFamily: "Arial Narrow, Impact, sans-serif",
  },
  {
    value: "stressFluent",
    label: "Stress Fluent",
    hint: "Cormorant display with Jost body",
    previewFamily: "Georgia, serif",
  },
];

const FONT_THEME_PACK: Record<FontThemeKey, StylePackKey> = {
  default: "classic",
  exercise: "energetic",
  meditation: "zen",
  diet: "classic",
  learning: "editorial",
  serif: "editorial",
  geometric: "energetic",
  humanist: "classic",
  power: "energetic",
  stressFluent: "calmPower",
};

export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value?: string | null): value is string {
  return typeof value === "string" && HEX_COLOR_PATTERN.test(value.trim());
}

export function resolveStylePackPreview(
  stylePack?: string | null,
  fontTheme?: string | null
): StylePackPreview {
  if (stylePack && STYLE_PACKS[stylePack as StylePackKey]) {
    return STYLE_PACKS[stylePack as StylePackKey];
  }

  const derived = fontTheme
    ? FONT_THEME_PACK[fontTheme as FontThemeKey]
    : undefined;
  return STYLE_PACKS[derived || "classic"];
}

export function resolvePreviewPalette(draft: PersonalizationDraft) {
  const pack = resolveStylePackPreview(draft.stylePack, draft.fontTheme);
  const surface = isHexColor(draft.surfaceColor)
    ? draft.surfaceColor
    : pack.palette.surface;
  return {
    background: isHexColor(draft.backgroundColor)
      ? draft.backgroundColor
      : pack.palette.background,
    surface,
    surfaceElevated: isHexColor(draft.surfaceElevatedColor)
      ? draft.surfaceElevatedColor
      : isHexColor(draft.surfaceColor)
        ? surface
        : pack.palette.surfaceElevated,
    textPrimary: isHexColor(draft.textColor)
      ? draft.textColor
      : pack.palette.textPrimary,
    textSecondary: isHexColor(draft.secondaryTextColor)
      ? draft.secondaryTextColor
      : pack.palette.textSecondary,
    textMuted: isHexColor(draft.mutedTextColor)
      ? draft.mutedTextColor
      : pack.palette.textMuted,
    surfaceBorder: isHexColor(draft.surfaceBorderColor)
      ? draft.surfaceBorderColor
      : pack.palette.surfaceBorder,
    divider: isHexColor(draft.dividerColor)
      ? draft.dividerColor
      : pack.palette.divider,
    primary: isHexColor(draft.primaryColor) ? draft.primaryColor : "#17233D",
    accent: isHexColor(draft.accentColor)
      ? draft.accentColor
      : isHexColor(draft.primaryColor)
        ? draft.primaryColor
        : "#17233D",
    secondary: isHexColor(draft.secondaryColor)
      ? draft.secondaryColor
      : "#E95400",
    onAccent: isHexColor(draft.onAccentColor)
      ? draft.onAccentColor
      : pack.palette.onPrimary,
  };
}

export function clampOptionalScale(
  value: number | string | null | undefined,
  min: number,
  max: number
): number | null {
  if (value === "" || value === null || value === undefined) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.min(max, Math.max(min, numeric));
}

export function normalizeOptionalColor(
  value: string | null | undefined
): string | null {
  return isHexColor(value) ? value.trim().toUpperCase() : null;
}

export const OPTIONAL_THEME_COLOR_FIELDS = [
  "accentColor",
  "backgroundColor",
  "surfaceColor",
  "surfaceElevatedColor",
  "surfaceBorderColor",
  "dividerColor",
  "textColor",
  "secondaryTextColor",
  "mutedTextColor",
  "onAccentColor",
] as const;

type CommunityPersonalizationFields = Pick<
  Community,
  | "primaryColor"
  | "secondaryColor"
  | "fontTheme"
  | "stylePack"
  | (typeof OPTIONAL_THEME_COLOR_FIELDS)[number]
  | "typographyScale"
  | "radiusScale"
>;

export function readPersonalizationDraft(
  source?: Partial<CommunityPersonalizationFields> | null
): PersonalizationDraft {
  return {
    primaryColor: source?.primaryColor || "",
    secondaryColor: source?.secondaryColor || "",
    accentColor: source?.accentColor || "",
    backgroundColor: source?.backgroundColor || "",
    surfaceColor: source?.surfaceColor || "",
    surfaceElevatedColor: source?.surfaceElevatedColor || "",
    surfaceBorderColor: source?.surfaceBorderColor || "",
    dividerColor: source?.dividerColor || "",
    textColor: source?.textColor || "",
    secondaryTextColor: source?.secondaryTextColor || "",
    mutedTextColor: source?.mutedTextColor || "",
    onAccentColor: source?.onAccentColor || "",
    fontTheme: source?.fontTheme || "",
    stylePack: source?.stylePack || "",
    typographyScale: source?.typographyScale ?? "",
    radiusScale: source?.radiusScale ?? "",
  };
}

export function buildPersonalizationFields(
  draft: PersonalizationDraft
): CommunityPersonalizationFields {
  return {
    primaryColor: draft.primaryColor.trim(),
    secondaryColor: draft.secondaryColor.trim(),
    fontTheme: draft.fontTheme.trim(),
    stylePack: draft.stylePack.trim(),
    accentColor: normalizeOptionalColor(draft.accentColor),
    backgroundColor: normalizeOptionalColor(draft.backgroundColor),
    surfaceColor: normalizeOptionalColor(draft.surfaceColor),
    surfaceElevatedColor: normalizeOptionalColor(
      draft.surfaceElevatedColor
    ),
    surfaceBorderColor: normalizeOptionalColor(draft.surfaceBorderColor),
    dividerColor: normalizeOptionalColor(draft.dividerColor),
    textColor: normalizeOptionalColor(draft.textColor),
    secondaryTextColor: normalizeOptionalColor(draft.secondaryTextColor),
    mutedTextColor: normalizeOptionalColor(draft.mutedTextColor),
    onAccentColor: normalizeOptionalColor(draft.onAccentColor),
    typographyScale: clampOptionalScale(draft.typographyScale, 0.85, 1.25),
    radiusScale: clampOptionalScale(draft.radiusScale, 0, 2),
  };
}

function channel(value: string): number {
  const normalized = Number.parseInt(value, 16) / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : Math.pow((normalized + 0.055) / 1.055, 2.4);
}

export function contrastRatio(foreground: string, background: string): number {
  if (!isHexColor(foreground) || !isHexColor(background)) return 0;
  const fg = foreground.slice(1);
  const bg = background.slice(1);
  const fgLum =
    0.2126 * channel(fg.slice(0, 2)) +
    0.7152 * channel(fg.slice(2, 4)) +
    0.0722 * channel(fg.slice(4, 6));
  const bgLum =
    0.2126 * channel(bg.slice(0, 2)) +
    0.7152 * channel(bg.slice(2, 4)) +
    0.0722 * channel(bg.slice(4, 6));
  return (Math.max(fgLum, bgLum) + 0.05) / (Math.min(fgLum, bgLum) + 0.05);
}
